<template>
    <div class="seedream-page">
        <!-- Image Generator Section -->
        <section ref="generatorSection" class="generator-section">
            <div class="container">
                <div class="generator-header">
                    <h1 class="generator-title">
                        {{ t('seedream-4-5.generatorTitle') }}
                    </h1>
                    <p class="generator-subtitle">
                        {{ t('seedream-4-5.generatorSubtitle') }}
                    </p>
                </div>

                <ImageGenerator
                    model-name="Seedream 4.5"
                    :default-tab="1"
                    @generate="handleImageGenerate"
                    model-key="SEEDREAM_4_5"
                />
            </div>
        </section>

        <!-- Core Features Section -->
        <ImageShowcaseSection
            :title="t('seedream-4-5.coreFeaturesTitle')"
            :subtitle="t('seedream-4-5.coreFeaturesSubtitle')"
            :features="coreFeatures"
        />

        <!-- How to Use Section -->
        <HowToUseSection
            :title="t('seedream-4-5.howToUseTitle')"
            :steps="howToUseSteps"
        />

        <!-- YouTube Videos Section -->
        <YouTubeCarouselSection
            :title="t('seedream-4-5.youtubeTitle')"
            :videos="youtubeVideos"
        />

        <!-- X Posts Section -->
        <TwitterPosts
            :title="t('seedream-4-5.xPostsTitle')"
            :tweet-urls="twitterTweetUrls"
        />

        <!-- Explore Other Models Section -->
        <ExploreModelsSection
            :title="t('seedream-4-5.exploreModelsTitle')"
            :models="exploreModels"
        />

        <!-- FAQ Section -->
        <FAQSection :title="t('seedream-4-5.faqTitle')" :faqs="faqItems" />

        <!-- CTA Section -->
        <CTASection
            :title="t('seedream-4-5.ctaTitle')"
            :description="t('seedream-4-5.ctaDescription')"
            :button-text="t('seedream-4-5.ctaButton')"
            :disclaimer="t('seedream-4-5.ctaDisclaimer')"
            @click="scrollToGenerator"
        />
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useHead, useRequestURL } from '#imports'
import ImageGenerator from '~/components/ImageGenerator.vue'
import TwitterPosts from '~/components/TwitterPosts.vue'
import {
    ImageShowcaseSection,
    HowToUseSection,
    YouTubeCarouselSection,
    ExploreModelsSection,
    FAQSection,
    CTASection,
} from '~/components/model/image'
import { useI18n } from '~/composables/useI18n'
import { useMultilingualSeo } from '~/composables/useMultilingualSeo'
import { useHowToSeo } from '~/composables/useHowToSeo'
import { analyticsEvent } from '~/utils/log-event-func'

const { t } = useI18n()
const requestUrl = useRequestURL()
const requestUrlHref = requestUrl.href

definePageMeta({
    layout: 'website',
})

// Ref for generator section
const generatorSection = ref<HTMLElement | null>(null)

// Scroll to generator function
const scrollToGenerator = () => {
    if (generatorSection.value) {
        generatorSection.value.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        })
    }
}

// 设置多语言 SEO Meta 标签
useMultilingualSeo({
    pageType: 'image_seedream_4_5',
})

// BreadcrumbList SEO
useHead({
    script: [
        {
            type: 'application/ld+json',
            innerHTML: computed(() =>
                JSON.stringify({
                    '@context': 'https://schema.org',
                    '@type': 'BreadcrumbList',
                    itemListElement: [
                        {
                            '@type': 'ListItem',
                            position: 1,
                            name: 'Home',
                            item: 'https://www.weryai.com.cn/',
                        },
                        {
                            '@type': 'ListItem',
                            position: 2,
                            name: t('seedream-4-5.generatorTitle'),
                            item: requestUrlHref,
                        },
                    ],
                })
            ),
        },
    ],
})

// Core Features Data
const coreFeatures = computed(() => [
    {
        title: t('seedream-4-5.feature1Title'),
        description: t('seedream-4-5.feature1Desc'),
        imageUrl: 'https://static.weryai.com/cms/seedream4.5_image_01.webp',
    },
    {
        title: t('seedream-4-5.feature2Title'),
        description: t('seedream-4-5.feature2Desc'),
        imageUrl: 'https://static.weryai.com/cms/seedream4.5_image_02.webp',
    },
    {
        title: t('seedream-4-5.feature3Title'),
        description: t('seedream-4-5.feature3Desc'),
        imageUrl: 'https://static.weryai.com/cms/seedream4.5_image_03.webp',
    },
    {
        title: t('seedream-4-5.feature4Title'),
        description: t('seedream-4-5.feature4Desc'),
        imageUrl: 'https://static.weryai.com/cms/seedream4.5_image_04.webp',
    },
])

// How to Use Steps Data
const howToUseSteps = computed(() => [
    {
        title: t('seedream-4-5.step1Title'),
        description: t('seedream-4-5.step1Description'),
    },
    {
        title: t('seedream-4-5.step2Title'),
        description: t('seedream-4-5.step2Description'),
    },
    {
        title: t('seedream-4-5.step3Title'),
        description: t('seedream-4-5.step3Description'),
    },
])

// HowTo SEO - 注入结构化数据
useHowToSeo(
    computed(() => t('seedream-4-5.howToUseTitle')),
    howToUseSteps
)

// YouTube Videos Data
const youtubeVideos = [
    {
        title: 'Seedream 4.5 Introduction',
        embedUrl: 'https://www.youtube.com/embed/Bg-x73xsLzY',
    },
    {
        title: 'Seedream 4.5 Tutorial',
        embedUrl: 'https://www.youtube.com/embed/tutcXoM0v3k',
    },
    {
        title: 'Seedream 4.5 Features',
        embedUrl: 'https://www.youtube.com/embed/QCOrrcbutHM',
    },
    {
        title: 'Seedream 4.5 Demo',
        embedUrl: 'https://www.youtube.com/embed/EFnvhqR1TQM',
    },
    {
        title: 'Seedream 4.5 Showcase',
        embedUrl: 'https://www.youtube.com/embed/eGm6JdfZisE',
    },
]

// Twitter Tweet URLs Data
const twitterTweetUrls = [
    'https://x.com/TruVoiceAmerica/status/1997280339866296474',
    'https://x.com/yves_disco/status/1998183184413188429',
    'https://x.com/takuma0083/status/1996814583118590304',
    'https://x.com/flymoon1337804/status/1998319141942391291',
    'https://x.com/takuma0083/status/1997496217920831933',
    'https://x.com/asheem01/status/1997679398841885080',
    'https://x.com/takuma0083/status/1997181594176790579',
    'https://x.com/itnavi2022/status/1996447251514929660',
    'https://x.com/25Kare4/status/1996977237283762223',
    'https://x.com/takuma0083/status/1998699216319684638',
    'https://x.com/iaPulse_/status/1996559141377315118',
    'https://x.com/seven_cuz/status/1997317603648520630',
]

// Explore Models Data
const exploreModels = computed(() => [
    {
        title: t('seedream-4-5.model1Title'),
        icon: 'https://static.weryai.com/cms/3ed8c9e1a1ec3b37133c608c292821cb.webp',
        path: '/image/nano-banana',
    },
    {
        title: t('seedream-4-5.model2Title'),
        icon: 'https://static.weryai.com/cms/88fca6eaf44c1b7c1e02caa0a061fe11.webp',
        path: '/image/qwen-image',
    },
    {
        title: t('seedream-4-5.model3Title'),
        icon: 'https://static.weryai.com/cms/98653aa87810721ed41e47d1df57b068.webp',
        path: '/image/dreamina',
    },
])

// FAQ Items Data
const faqItems = computed(() => [
    {
        question: t('seedream-4-5.faq1Question'),
        answer: t('seedream-4-5.faq1Answer'),
    },
    {
        question: t('seedream-4-5.faq2Question'),
        answer: t('seedream-4-5.faq2Answer'),
    },
    {
        question: t('seedream-4-5.faq3Question'),
        answer: t('seedream-4-5.faq3Answer'),
    },
    {
        question: t('seedream-4-5.faq4Question'),
        answer: t('seedream-4-5.faq4Answer'),
    },
    {
        question: t('seedream-4-5.faq5Question'),
        answer: t('seedream-4-5.faq5Answer'),
    },
    {
        question: t('seedream-4-5.faq6Question'),
        answer: t('seedream-4-5.faq6Answer'),
    },
])

// Image Generation Handler
const handleImageGenerate = (data: {
    prompt: string
    image?: File
    tabType: 'text-to-image' | 'image-to-image'
}) => {
    // console.log('Image generation data:', data)
}

onMounted(() => {
    analyticsEvent('view_model_page_common', {
        model_name: 'seedream_4_5',
        type: 'image',
    })
})
</script>

<style lang="less" scoped>
@import '~/assets/css/howToUse.css';
</style>
